import {Button} from 'platform/components';
import {HStack, Show, Text} from 'platform/foundation';

import {KeyboardEventHandler} from 'react';

import {isNil, isNotNil} from 'ramda';

import i18n from '@dms/i18n';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import type {AiMessageComposerController} from '../useAiMessageComposer';
import {AiComposerContextList} from './AiComposerContextList';
import {AiComposerSourcesButton} from './AiComposerSourcesButton';

interface AiComposerAppliedStatusProps extends RequiredTestIdProps {
  controller: AiMessageComposerController;
}

/**
 * One line in the editor footer once a proposal is accepted: that AI wrote it, Undo, and what it
 * used. It replaces the bar that repeated the refinements, the language and a change excerpt
 * (Patrik, 2026-10-06); refinements run from AI Generate again.
 */
export function AiComposerAppliedStatus(props: AiComposerAppliedStatusProps) {
  const controller = props.controller;
  if (
    !controller.isOpen ||
    !controller.canUndo ||
    isNil(controller.undoStep) ||
    isNotNil(controller.proposal) ||
    controller.isPending
  ) {
    return null;
  }

  const handleKeyDownCapture: KeyboardEventHandler<HTMLDivElement> = (event) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      event.preventDefault();
      controller.onEscape();
    }
  };

  return (
    <div role="group" aria-label={i18n.t('aiAssistant.drafts.templateEdit.applied')}>
      <HStack spacing={2} align="center">
        <Text size="xSmall" color="secondary">
          {i18n.t('aiAssistant.drafts.templateEdit.applied')}
        </Text>
        <Button
          variant="ghostLink"
          size="small"
          leftIcon="action/rotateLeft"
          title={i18n.t('entity.documentTemplateEditor.shortcuts.labels.undo')}
          onClick={controller.onUndo}
          data-testid={suffixTestId('undo', props)}
        />
        <Show when={isNotNil(controller.appliedProvenance)}>
          <AiComposerSourcesButton
            isOpen={controller.activeSources === 'applied'}
            onOpenChange={(isOpen) => controller.onSourcesChange(isOpen ? 'applied' : null)}
            onKeyDownCapture={handleKeyDownCapture}
            data-testid={suffixTestId('appliedSources', props)}
          >
            <AiComposerContextList
              usedContext={controller.appliedProvenance?.usedContext ?? []}
              unavailableContext={controller.appliedProvenance?.unavailableContext ?? []}
              languageSource={controller.appliedProvenance?.languageSource}
              locale={controller.appliedProvenance?.locale}
              title={i18n.t('general.labels.source')}
              data-testid={suffixTestId('applied', props)}
            />
          </AiComposerSourcesButton>
        </Show>
      </HStack>
    </div>
  );
}
